export interface StreetNumber {
  street: string;
  number: string;
}

// The leading road-type word each spelling of a street may or may not carry: `C/ Del Encanto` and
// `Calle del Encanto` name the same street, so the type is dropped before comparing. Matched as a
// whole first word after punctuation is stripped, so `c/` arrives here as `c` and `av.` as `av`.
const ROAD_TYPES = new Set([
  'c',
  'calle',
  'avenida',
  'av',
  'avda',
  'plaza',
  'pza',
  'paseo',
  'po',
  'carrer',
  'ronda',
  'camino',
  'ctra',
  'travesia',
]);

// A Spanish postal code: five digits. No street number is that long, so a first digit group of this
// size means the address has no street number and the code is what came first.
const POSTAL_CODE_DIGITS = 5;

// «s/n» (sin número), with or without spaces around the slash.
const WITHOUT_NUMBER = /\bs\s*\/\s*n\b/;

// Reduces a free-text address to the two parts that identify a building: its street and its number.
// Null when there is no number — a street name alone cannot tell two buildings apart.
//
// The FIRST digit group is the number, and everything after it is ignored. That is deliberate: the
// titular's address names their own portal and floor (`… 18 Portal 8, 3º B`), the building's names
// its main entrance (`… 18`), and comparing whole strings would flag every titular who lives above
// the ground floor. The first number is the street number in both, and the postal code and city
// always come later.
//
// Unless there is no street number at all, which is why two first groups do NOT count: one marked
// `s/n` before it (a later portal is not the street number), and one of five digits (the postal
// code). Read as numbers, both made an address without one differ from every building on its street.
//
// NFKD and not NFD: besides splitting accents off their letters, it also folds the ordinal `º` to
// `o`, so `Pº` reaches the road-type list as `po`.
export function normalizeStreetNumber(address: string): StreetNumber | null {
  const text = address
    .normalize('NFKD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
  const match = /\d+/.exec(text);
  if (match === null) return null;
  if (match[0].length === POSTAL_CODE_DIGITS) return null;
  if (WITHOUT_NUMBER.test(text.slice(0, match.index))) return null;

  const words = text
    .slice(0, match.index)
    .replace(/[^\p{L}\s]+/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (ROAD_TYPES.has(words[0] ?? '')) words.shift();

  return { street: words.join(' '), number: match[0] };
}
